<template lang="pug">
    .ma-sm-2(v-if="validOrganizationId")

        .text-center(v-if="isLoading")
            v-progress-circular(
                :size='100'
                :width='10'
                color='var(--volie-blue)'
                indeterminate
                style="margin-top: 100px;"
            )

        div(v-else)
            v-container
                template(v-if="isManualMode && newManualModeEnabled")
                    v-row(style="margin: 15px 0;")
                        v-select(
                            :items='countries'
                            :item-text="dialerCountryDisplay"
                            item-value="iso_two"
                            label='Country'
                            v-model="selectedCountry"
                            dense
                            outlined
                            color="primary"
                            style="max-width: 80px; margin-right: 8px;"
                            class="manual-country-select"
                        )
                            template(v-slot:item='data')
                                v-list-item-content
                                    v-list-item-title {{ data.item.name }} ({{ data.item.dialing_code }})
                        v-text-field(
                            v-model="outboundNumber"
                            label="Phone Number To Dial"
                            dense
                            outlined
                            color="primary"
                            :hint="outboundNumberType"
                            class="manual-dialer-field"
                            :error-messages="outboundNumberErrorMessage"
                            v-on:keypress="outboundNumberChange"
                        )
                            template(v-slot:append)
                                v-tooltip(bottom)
                                    template(v-slot:activator="{ on, attrs }")
                                        v-btn(
                                            color="success"
                                            icon
                                            v-bind="attrs"
                                            v-on="on"
                                            :disabled="!outboundNumberValid"
                                            @click="startDialForManualMode()"
                                            class="dial-btn"
                                        )
                                            v-icon mdi-phone-forward
                                    span Dial Number
                    v-divider

                v-form
                    v-list(two-line)
                        template(v-if="renderOrganizationsDropdown")
                            v-list-item
                                v-list-item-icon
                                    v-icon(color='primary')
                                        | mdi-office-building
                                v-list-item-content
                                    v-list-item-title
                                        v-row
                                            v-col.py-2(cols='12')
                                                v-autocomplete(
                                                    :items='organizationUsers'
                                                    item-text='organization_name'
                                                    item-value='organization_id'
                                                    label='Organization'
                                                    v-model='overrideOrganizationId'
                                                )

                        template
                            v-list-item
                                v-list-item-icon
                                    v-icon(color='primary')
                                        | mdi-account
                                v-list-item-content
                                    v-list-item-title
                                        v-row
                                            v-col.py-2(cols='6')
                                                v-text-field(
                                                    label='First Name'
                                                    type='text'
                                                    v-model="firstName"
                                                    @keydown.enter="search()"
                                                )
                                            v-col.py-2(cols='6')
                                                v-text-field(
                                                    label='Last Name'
                                                    type='text'
                                                    v-model="lastName"
                                                    @keydown.enter="search()"
                                                )
                    
                            v-list-item
                                v-list-item-icon
                                    v-icon(color='primary')
                                        | mdi-phone
                                v-list-item-content
                                    v-list-item-title
                                        v-row(align="center")
                                            v-col.py-2(cols='6')
                                                 v-select(
                                                    :items='countries'
                                                    :item-text="countryDisplay"
                                                    item-value="iso_two"
                                                    label='Country'
                                                    v-model="pnCountryIsoTwo"
                                                )
                                            v-col.py-2(cols='6')
                                                v-text-field(
                                                    label='Phone Number'
                                                    type='text'
                                                    v-model="phoneNumber"
                                                    v-on:keypress="phoneNumberChange"
                                                    @keydown.enter="search()"
                                                )

                            v-list-item
                                v-list-item-icon
                                    v-icon(color='primary')
                                        | mdi-email
                                v-list-item-content
                                    v-list-item-title
                                        v-row(align="center")
                                            v-col.py-2(cols='12')
                                                v-text-field(
                                                    label='Email Address'
                                                    type='text'
                                                    v-model="emailAddress"
                                                    @keydown.enter="search()"
                                                )

                            v-list-item
                                v-list-item-icon
                                    v-icon(color='primary')
                                        | mdi-car
                                v-list-item-content
                                    v-list-item-title
                                        v-row
                                            v-col.py-2(cols='12')
                                                v-text-field(
                                                    label='Vehicle VIN'
                                                    type='text'
                                                    v-model="vehicleVin"
                                                    @keydown.enter="search()"
                                                )

                    v-row(align="center")
                        v-col(cols='12')
                            .text-right
                                v-btn.mr-4(
                                    v-if="renderCancelSwitchCustomerButton"
                                    color="warning"
                                    @click='cancelSwitchCustomer();'
                                )
                                    v-icon mdi-cancel
                                    | Cancel Switch Customer
                                v-btn.mr-4(
                                    @click='resetSearch();'
                                )
                                    v-icon mdi-refresh
                                    | Reset
                                v-btn.mr-4(
                                    color='info'
                                    @click='search();'
                                    :disabled="disableSearchButton"
                                )
                                    v-icon mdi-magnify
                                    | Search

            v-alert(
                v-if="renderSearchAlert"
                outlined
                type='info'
                dense
                border='left'
                style="margin-top: 16px;"
            )
                | Search {{ isVehicleSearch ? 'vehicles' : 'customers' }}

            div(
                v-else-if="renderCustomers"
                v-for="(result, idx) in results"
                :key="idx"
            )
                v-card.mx-auto.my-sm-4(
                    outlined
                    shaped
                )
                    template(v-if="isVehicleSearch ? validCustomerVehicleResult(result) : validCustomerResult(result)")
                        v-list(two-line)
                            template(v-if="isVehicleSearch")
                                v-list-item
                                    v-list-item-content
                                        v-list-item-title(v-if='isVehicleSearch' v-text='customerVehicleYearMakeModel(result)' style='font-weight: 500;')
                                        v-list-item-subtitle.text--primary {{ result.customer_matcher.organization_name }}
                                        v-list-item-subtitle.text--primary(v-text='customerName(result)')
                                    v-list-item-action
                                        v-tooltip(bottom)
                                            template(v-slot:activator='{ on, attrs }')
                                                v-btn(
                                                    outlined
                                                    fab
                                                    x-small
                                                    color="success"
                                                    @click="createCustomerVehicle(result.vehicle);"
                                                    v-bind='attrs'
                                                    v-on='on'
                                                )
                                                    v-icon
                                                        | mdi-plus
                                            span Add vehicle to customer's profile

                            template(v-else)
                                v-list-item
                                    v-list-item-content
                                        v-list-item-title
                                            strong {{ result.customer_matcher.organization_name }}
                                        v-list-item-subtitle.text--primary(v-text='customerName(result)')

                                    v-list-item-action(v-if="validSwitchCustomer")
                                        v-tooltip(bottom v-if="result.customer_matcher.customer_id !== currentCustomerID")
                                            template(v-slot:activator='{ on, attrs }')
                                                v-btn(
                                                    fab
                                                    x-small
                                                    color="success"
                                                    @click="switchCustomerForCampaignCustomerActive(result.customer_matcher.customer_id);"
                                                    v-bind='attrs'
                                                    v-on='on'
                                                    :disabled="isSupervisor"
                                                )
                                                    v-icon
                                                        | mdi-swap-horizontal
                                            span Switch customer

                                        .font-italic.info--text(v-else) CURRENT CUSTOMER
                                
                                    v-list-item-action(v-else-if="selectCustomerMode")
                                        v-tooltip(bottom)
                                            template(v-slot:activator='{ on, attrs }')
                                                v-btn(
                                                    outlined
                                                    fab
                                                    x-small
                                                    color="success"
                                                    @click="$emit('select-customer', result.customer_matcher)"
                                                    v-bind='attrs'
                                                    v-on='on'
                                                )
                                                    v-icon
                                                        | mdi-check
                                            span Select customer

                                    v-list-item-action(v-else-if="validEnrollCampaignCustomer")
                                        v-tooltip(bottom)
                                            template(v-slot:activator='{ on, attrs }')
                                                v-btn(
                                                    outlined
                                                    fab
                                                    x-small
                                                    color="success"
                                                    @click="handleEnrollCampaignCustomer(result);"
                                                    v-bind='attrs'
                                                    v-on='on'
                                                    :disabled="isSupervisor"
                                                )
                                                    v-icon
                                                        | mdi-check
                                            span Enroll customer

                                    v-list-item-action(v-else-if="validViewProfile")
                                        v-tooltip(bottom)
                                            template(v-slot:activator='{ on, attrs }')
                                                v-btn(
                                                    outlined
                                                    fab
                                                    x-small
                                                    color="success"
                                                    @click="viewProfile(result.customer_matcher.customer_id);"
                                                    v-bind='attrs'
                                                    v-on='on'
                                                )
                                                    v-icon
                                                        | mdi-eye
                                            span View profile

                            template(v-if="validCustomerPhoneNumber(result)")
                                v-divider(inset)
                                ListItem(
                                    v-if="result.customer_matcher.cell_phone && result.customer_matcher.cell_phone.length > 0"
                                    default-title="N/a"
                                    icon="mdi-phone"
                                    indent
                                    subtitle="Cell"
                                    :title="result.customer_matcher.cell_phone | tel"
                                )
                                    template(v-slot:title)
                                        CopyablePhoneNumber(:phone-number="{ phone_number_phone_number: result.customer_matcher.cell_phone }")

                                ListItem(
                                    v-if="result.customer_matcher.home_phone && result.customer_matcher.home_phone.length > 0"
                                    default-title="N/a"
                                    :icon="!(result.customer_matcher.cell_phone && result.customer_matcher.cell_phone.length > 0) ? 'mdi-phone' : null"
                                    indent
                                    subtitle="Home"
                                    :title="result.customer_matcher.home_phone | tel"
                                )
                                    template(v-slot:title)
                                        CopyablePhoneNumber(:phone-number="{ phone_number_phone_number: result.customer_matcher.home_phone }")

                                ListItem(
                                    v-if="result.customer_matcher.work_phone && result.customer_matcher.work_phone.length > 0"
                                    default-title="N/a"
                                    :icon="!(result.customer_matcher.cell_phone && result.customer_matcher.cell_phone.length > 0) && !(result.customer_matcher.home_phone && result.customer_matcher.home_phone.length > 0) ? 'mdi-phone' : null"
                                    indent
                                    subtitle="Work"
                                    :title="result.customer_matcher.work_phone | tel"
                                )
                                    template(v-slot:title)
                                        CopyablePhoneNumber(:phone-number="{ phone_number_phone_number: result.customer_matcher.work_phone }")

                            template(v-if="result.customer_matcher.email_address_one && result.customer_matcher.email_address_one.length > 0")
                                v-divider(inset)
                                ListItem(
                                    default-title="N/a"
                                    icon="mdi-email"
                                    indent
                                    subtitle="Email"
                                    :title="result.customer_matcher.email_address_one"
                                )

                            template(v-if="validCustomerAddress(result)")
                                v-divider(inset)
                                ListItem(
                                    default-title="N/a"
                                    icon="mdi-map-marker"
                                    indent
                                    subtitle="Address"
                                    :title="result.customer_matcher.street_one"
                                )
                                    template(v-slot:title)
                                        AddressPostalFormat(
                                            :street-one="result.customer_matcher.street_one"
                                            :street-two="result.customer_matcher.street_two"
                                            :city="result.customer_matcher.city"
                                            :region="result.customer_matcher.region"
                                            :postal-code="result.customer_matcher.postal_code"
                                            :country="result.customer_matcher.country_name"
                                        )

                            template(v-if="validCustomerVehicles(result)")
                                v-divider(inset)
                                ListItem(
                                    v-for="(veh, jdx) in result.vehicles"
                                    :key="jdx"
                                    default-title="N/a"
                                    :icon="jdx === 0 ? 'mdi-car' : null"
                                    indent
                                    :subtitle="veh.vin"
                                    :subtitle-two="vehicleSubtitleTwo(veh)"
                                    :title="vehicleYearMakeModel(veh)"
                                )
                                    template(v-slot:subtitle)
                                        | VIN:
                                        CopyableText(
                                            v-if="veh.vin"
                                            :text="(veh.vin || '')"
                                        )
                                        template(v-else) N/a
                                            | {{vehicleSubtitleTwo(veh)}}

                            template(v-if="validCustomerEventSummary(result) && result.event_summary.list_items && result.event_summary.list_items.length > 0")
                                v-divider(inset)
                                ListItem(
                                    v-for="(item, jdx) in result.event_summary.list_items"
                                    :key="jdx + '-event'"
                                    :icon="jdx === 0 ? 'mdi-information-box' : null"
                                    indent
                                    :subtitle="item.subtitle"
                                    :title="item.title"
                                )

                v-divider(
                    v-if='idx < results.length - 1'
                    :key='idx'
                )

            v-alert(
                v-else-if="renderNoResultsAlert"
                outlined
                type='info'
                dense
                border='left'
                style="margin-top: 16px;"
            )
                | Unable to find any results. Please adjust your search criteria.

</template>

<script lang="ts">
    import Vue from 'vue';
    import VolieState  from "@/store";

    import * as Sentry from "@sentry/vue";

    // Components
    import AddressPostalFormat from "@/components/shared/addresses/address_postal_format.vue";
    import CopyablePhoneNumber from '@/components/shared/copyable_phone_number.vue';
    import CopyableText from '@/components/shared/copyable_text.vue';
    import ListItem from '@/components/shared/list_item.vue';

    // Data
    import { MatchCustomers } from "@/data";

    // Helpers
    import { cleanupPhoneNumber } from "@/helpers/phone_number";
    import { concatVehicleYearMakeModel, formatNumber, titleize } from "@/helpers/general";
    import { formatName } from "@/helpers/name";

    // Types
    import { VehicleYearMakeModelObj } from "@/helpers/general";

    export default Vue.extend({
        data() {
            return {
                results: [],
                emailAddress: null,
                firstName: null,
                isLoading: false,
                isSearched: false,
                lastName: null,
                middleName: null,
                phoneNumber: null,
                pnCountryIsoTwo: "US",
                outboundNumber: null,
                outboundNumberType: null,
                outboundNumberErrorMessage: null,
                outboundNumberValid: false,
                overrideOrganizationId: null,
                selectedCountry: "US",
                vehicleVin: null,
                validatedOutboundNumber: null,
            }
        },
        methods: {
            buildCustomerVehicleResults(customers) {
                if (!(customers?.length > 0)) {
                    return;
                }

                const results = [];
                for (let i = 0; i < customers.length; i++) {

                    const customerVehicles = customers[i].vehicles;
                    if (customerVehicles?.length > 0) {
                        for (let j = 0; j < customerVehicles.length; j++) {

                            const customerVehicle = customerVehicles[j]
                            if (customerVehicle?.vehicle_make?.length > 0 && customerVehicle?.vehicle_model?.length > 0 && customerVehicle?.vehicle_model_year?.length > 0) {

                                const result = customers[i];
                                result.vehicle = customerVehicle;
                                results.push(result);
                            }
                        }
                    }
                }

                return results;
            },
            buildCustomerEventListItems() {
                if (!(this.results?.length > 0)) {
                    return;
                }

                // loop
                for (let i = 0; i < this.results.length; i++) {
                    const result = this.results[i];
                    if (!result || !(result.event_summary?.customer_id > 0)) {
                        continue;
                    }

                    // init
                    result.event_summary.list_items = [];

                    // communication disposition
                    if (result.event_summary?.last_communication_disposition_id > 0) {
                        result.event_summary.list_items.push({
                            subtitle: "Last Disposition",
                            title: `${result.event_summary.last_cd_created_date} | ${titleize(result.event_summary.last_cd_communication_channel)} | ${result.event_summary.last_cd_disposition}`,
                        });
                    }

                    // upcoming appointments
                    if (result.event_summary?.upcoming_appointment_dates?.length > 0) {
                        result.event_summary.list_items.push({
                            subtitle: "Upcoming Appointment(s)",
                            title: result.event_summary.upcoming_appointment_dates,
                        });
                    }

                    // last lead
                    if (result.event_summary?.last_prospect_date?.length > 0) {
                        result.event_summary.list_items.push({
                            subtitle: "Last Lead Date",
                            title: result.event_summary.last_prospect_date,
                        });
                    }

                    // last sale
                    if (result.event_summary?.last_sale_date?.length > 0) {
                        result.event_summary.list_items.push({
                            subtitle: "Last Sale Date",
                            title: result.event_summary.last_sale_date,
                        });
                    }

                    // last service
                    if (result.event_summary?.last_service_date?.length > 0) {
                        result.event_summary.list_items.push({
                            subtitle: "Last Service Date",
                            title: result.event_summary.last_service_date,
                        });
                    }
                }
            },
            countryDisplay(item) {
                return item?.name + ' (' + item?.dialing_code + ")";
            },
            dialerCountryDisplay(item) {
                return item?.dialing_code;
            },
            customerName(result) {
                return formatName([result?.customer_matcher?.first_name, result?.customer_matcher?.middle_name, result?.customer_matcher?.last_name]);
            },
            customerVehicleYearMakeModel(result) {

                const vehObj = {
                    vehicleModelYear: result?.vehicle?.vehicle_model_year,
                    vehicleMake:      result?.vehicle?.vehicle_make,
                    vehicleModel:     result?.vehicle?.vehicle_model,
                }

                return concatVehicleYearMakeModel(vehObj as VehicleYearMakeModelObj);
            },
            handleEnrollCampaignCustomer(result) {
                const params = {
                    campaign_customer_id: result.campaign_customer_id,
                    next_attemptable_id: null,
                    next_attemptable_type: null,
                    next_attemptable_value: null,
                    noticeable_id: null,
                    noticeable_type: null,
                    vehicle_id: null,
                }
                if (this.call?.id > 0 && this.call?.direction === "inbound") {
                    params.noticeable_id = this.call?.id || 0;
                    params.noticeable_type = "call";
                }

                this.enrollCampaignCustomer(result.customer_matcher.customer_id, params);
            },
            init() {

                // Init
                let autoSearch = false;

                // initial (no auto search)
                this.firstName = this.initialFirstName?.length > 0 ? this.initialFirstName : null;
                this.emailAddress = this.initialEmailAddress?.length > 0 ? this.initialEmailAddress : null;
                this.lastName = this.initialLastName?.length > 0 ? this.initialLastName : null;

                // Initial: phone number (auto search on phone number)
                if (this.initialPhoneNumber?.length > 0) {

                    // Validate
                    const cleaned = cleanupPhoneNumber(this.initialPhoneNumber, "");
                    if (cleaned?.valid === true) {

                        // Set
                        autoSearch = true;
                        this.phoneNumber = cleaned.phoneNumber.replace(cleaned.dialingCode, "");
                        this.pnCountryIsoTwo = cleaned.countryIso;
                    }
                }

                return autoSearch ? this.search() : null;
            },
            outboundNumberChange(e) {
                // Ensure only numbers are entered
                if (isNaN(e.key)) {
                    e.preventDefault();
                }
            },
            phoneNumberChange(e) {

                // Ensure only numbers are entered
                if (isNaN(e.key)) {
                    e.preventDefault();
                }
            },
            resetSearch() {
                this.results = [];
                this.emailAddress = null;
                this.firstName = null;
                this.lastName = null;
                this.middleName = null;
                this.phoneNumber = null;
                this.pnCountryIsoTwo = "US";
                this.vehicleVin = null;
                this.isSearched = false;
            },
            search() {

                // Init
                const options = {
                    organization_id: this.organizationId,
                    cell_phone: this.phoneNumber,
                    email_address: this.emailAddress,
                    first_name: this.firstName,
                    last_name: this.lastName,
                    limit: 10,
                    middle_name: this.middleName,
                    vehicle_vin: this.vehicleVin,
                }

                // if we are rendering organization select, then use override
                if (this.validOrganizationUsers && this.overrideOrganizationId > 0) {
                    options.organization_id = this.overrideOrganizationId;
                }

                this.isLoading = true;
                MatchCustomers(options).then((response) => {
                    if (response?.Data?.customers?.length > 0) {
                        if (this.isVehicleSearch) {
                            this.results = this.buildCustomerVehicleResults(response.Data.customers);
                        } else {
                            this.results = response.Data.customers;
                            this.buildCustomerEventListItems();
                        }
                    } else {
                        this.results = [];
                    }
                    this.isSearched = true;
                    // If dialing out from manual mode, enroll customer automatically if there's only one result
                    if (this.forceValidatedOutboundNumber?.length > 0 && this.results?.length === 1) {
                        // do nothing
                    } else {
                        // only need to kill loader if displaying results. 
                        // In case of manual mode single result auto-enroll, the loader will disappear when the component is destroyed
                        this.isLoading = false;
                    }

                }).catch((err) => {
                    Sentry.captureException(err)
                });
            },
            startDialForManualMode() {
                // validate that we are in manual mode
                if (!this.isManualMode) {
                    return;
                }

                // validate that we have a good validated outbound number to dial on
                if (!this.outboundNumberValid || !(this.validatedOutboundNumber?.length > 0)) {
                    return;
                }

                // Search on validated outbound number
                const cleaned = cleanupPhoneNumber(this.validatedOutboundNumber, "");
                if (!cleaned?.valid) {
                    return;
                }

                // Set
                this.phoneNumber = cleaned.phoneNumber.replace(cleaned.dialingCode, "");
                this.pnCountryIsoTwo = cleaned.countryIso;
                this.search();

                // Start dial out from call screen
                this.beginManualModeOutboundDialWhileAlreadyInManualMode(this.validatedOutboundNumber);

            },
            validCustomerAddress(result) {
                return result?.customer_matcher?.street_one?.length > 0 || result?.customer_matcher?.city?.length > 0 || result?.customer_matcher?.region?.length > 0 || result?.customer_matcher?.postal_code?.length > 0;
            },
            validCustomerResult(result) {
                return result?.customer_matcher?.customer_id > 0;
            },
            validCustomerPhoneNumber(result) {
                return result?.customer_matcher?.cell_phone?.length > 0 || result?.customer_matcher?.home_phone?.length > 0 || result?.customer_matcher?.work_phone?.length > 0;
            },
            validCustomerVehicleResult(result) {
                return this.validCustomerResult(result) && result?.vehicle?.vehicle_id > 0 && result?.vehicle?.vehicle_make?.length > 0 && result?.vehicle?.vehicle_model?.length > 0 && result?.vehicle?.vehicle_model_year?.length > 0;
            },
            validCustomerVehicles(result) {
                return result?.vehicles?.length > 0;
            },
            validCustomerEventSummary(result) {
                return result?.event_summary?.customer_id > 0;
            },
            validateOutboundNumber() {
                if (this.outboundNumber?.length > 0) {
                    const cleanPN = cleanupPhoneNumber(this.outboundNumber, this.selectedCountry);
                    this.outboundNumberErrorMessage = cleanPN.valid === true ? null : "Please enter a valid phone number";
                    this.outboundNumberType = cleanPN.valid === true ? cleanPN.numberType[0].toUpperCase() + cleanPN.numberType.slice(1) : null;
                    this.outboundNumberValid = cleanPN.valid === true;
                    this.validatedOutboundNumber = cleanPN.valid === true ? cleanPN.phoneNumber : null;
                } else {
                    this.outboundNumberErrorMessage = null;
                    this.outboundNumberType = null;
                    this.validatedOutboundNumber = null;
                }
            },
            vehicleSubtitleTwo(vehicle) {

                // init
                const texts = [];

                if (vehicle?.customer_vehicle_id > 0) {

                    // last service
                    if (vehicle.friendly_last_service_date?.length > 0) {
                        texts.push("Last Service: " + vehicle.friendly_last_service_date);
                    }

                    // est odometer
                    if (vehicle.estimated_current_odometer > 0) {
                        texts.push("Est Odometer: " + formatNumber(vehicle.estimated_current_odometer));
                    }

                    // est odometer last service
                    if (vehicle.estimated_odometer_since_last_service > 0) {
                        texts.push("Est Odometer Since Last Service: " + formatNumber(vehicle.estimated_odometer_since_last_service));
                    }
                }

                return texts.join(", ");
            },
            vehicleYearMakeModel(vehicle) {
                if (!(vehicle?.customer_vehicle_id > 0)) {
                    return null;
                }

                const vehObj = {
                    vehicleModelYear: vehicle.vehicle_model_year,
                    vehicleMake:      vehicle.vehicle_make,
                    vehicleModel:     vehicle.vehicle_model,
                }

                return concatVehicleYearMakeModel(vehObj as VehicleYearMakeModelObj);
            },
        },
        watch: {
            "outboundNumber"() {
                this.validateOutboundNumber();
            },
            "call"() {
                if ((this.call?.id > 0 && this.call.direction !== "inbound") && (this.forceValidatedOutboundNumber?.length > 0 && this.results?.length === 1)) {
                    // auto enrolling customer for manual mode outbound dials from watcher AFTER call is set
                    this.handleEnrollCampaignCustomer(this.results[0]);
                };
            },
        },
        computed: {
            callIsInbound() {
                return this.call?.id > 0 && this.call?.direction === "inbound";
            },
            countries() {
                return VolieState.countries();
            },
            currentCustomerID() {
                return this.currentCampaignCustomer?.customer_id;
            },
            currentUser() {
                return VolieState.currentUser();
            },
            newManualModeEnabled() {
                return this.currentUser?.account?.new_manual_mode_enabled === true;
            },
            disableSearchButton() {

                // First name
                if (this.firstName?.length > 0) {
                    return false;
                }

                // Middle name
                if (this.middleName?.length > 0) {
                    return false;
                }

                // Last name
                if (this.lastName?.length > 0) {
                    return false;
                }

                // Vehicle vin
                if (this.vehicleVin?.length === 17) {
                    return false;
                }

                // Email address
                if (this.emailAddress?.length > 0) {
                    return false;
                }

                // Phone number
                if (this.phoneNumber?.length > 0) {
                    return false;
                }

                return true;
            },
            renderCustomers() {
                return !this.isLoading && this.isSearched && this.validResults;
            },
            renderNoResultsAlert() {
                return !this.isLoading && this.isSearched && !this.validResults;
            },
            renderOrganizationsDropdown() {
                return this.validOrganizationUsers && (this.isSwitchingCustomer || this.callIsInbound); // only allow switch if inbound call or we are deliberately trying to switch customers
            },
            renderSearchAlert() {
                return !this.isLoading && !this.isSearched;
            },
            renderCancelSwitchCustomerButton() {
                return this.isSwitchingCustomer && this.validCancelSwitchCustomer;
            },
            validCancelSwitchCustomer() {
                return this.cancelSwitchCustomer !== undefined && this.cancelSwitchCustomer !== null;
            },
            validCurrentCampaignCustomer() {
                return this.currentCampaignCustomer?.campaign_customer_active_id > 0;
            },
            validEnrollCampaignCustomer() {
                return this.enrollCampaignCustomer !== undefined && this.enrollCampaignCustomer !== null && !this.isSwitchingCustomer;
            },
            validResults() {
                return this.results?.length > 0;
            },
            validOrganizationId() {
                return (this.organizationId > 0);
            },
            validOrganizationUsers() {
                return this.organizationUsers?.length > 0;
            },
            validSwitchCustomer() {
                return this.validCurrentCampaignCustomer && this.switchCustomerForCampaignCustomerActive !== undefined && this.switchCustomerForCampaignCustomerActive !== null && this.isSwitchingCustomer === true;
            },
            validViewProfile() {
                return this.viewProfile !== undefined && this.viewProfile !== null;
            },
        },
        components: {
            AddressPostalFormat,
            CopyablePhoneNumber,
            CopyableText,
            ListItem,
        },
        props: {
            beginManualModeOutboundDialWhileAlreadyInManualMode: Function,
            call: Object,
            cancelSwitchCustomer: Function,
            createCustomerVehicle: Function,
            currentCampaignCustomer: Object,
            enrollCampaignCustomer: Function,
            forceValidatedOutboundNumber: String,
            initialEmailAddress: String,
            initialFirstName: String,
            initialLastName: String,
            initialPhoneNumber: String,
            isSupervisor: Boolean,
            isSwitchingCustomer: Boolean,
            isVehicleSearch: Boolean,
            isManualMode: Boolean,
            organizationId: Number,
            organizationUsers: Array,
            switchCustomerForCampaignCustomerActive: Function,
            selectCustomerMode: Boolean,
            viewProfile: Function,
        },
        destroyed() {
        },
        created() {
            this.overrideOrganizationId = this.organizationId;
            this.init();
        },
    });
</script>

<style lang="scss" scoped>
.dial-btn {
    margin-top: -8px;
    height: 36px;
    width: 36px;
}
.manual-country-select {
    .v-input__slot {
        border-radius: 8px;
        transition: all 0.3s ease;
        &:hover {
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
        }
    }
    .v-input__prepend-outer {
        margin-right: 8px;
    }
}

.manual-dialer-field >>> .v-input__append-outer {
    display: flex;
    align-items: center;
    height: 100%;
}
</style>
